import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import type { QueryClient } from "@tanstack/react-query";
import { createQueryI18nBackend } from "./query-i18n-backend";

import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  getStoredLanguage,
  setStoredLanguage,
  type SupportedLanguage,
} from "./language-storage";

export { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, type SupportedLanguage };

let languageChangedHandler: ((language: string) => void) | null = null;

export const DEFAULT_NAMESPACES = [
  "common",
  "admin",
  "appointments",
  "assist",
  "auth",
  "checkup",
  "dashboard",
  "doctors",
  "gamification",
  "lab-tests",
  "collect-data",
  "medications",
  "mind",
  "move",
  "nutrition",
  "pulse",
  "reports",
  "rewards",
  "sharing",
  "sleep",
  "travel-health",
  "users",
  "vaccines",
  "vital-signs",
] as const;

function enableLanguagePersistence(): void {
  if (languageChangedHandler) return;

  languageChangedHandler = (language: string) => {
    setStoredLanguage(language);
  };

  i18next.on("languageChanged", languageChangedHandler);
}

function resetLanguagePersistence(): void {
  if (!languageChangedHandler) return;

  i18next.off("languageChanged", languageChangedHandler);
  languageChangedHandler = null;
}

export function initI18n(queryClient: QueryClient) {
  resetLanguagePersistence();
  enableLanguagePersistence();

  return i18next
    .use(createQueryI18nBackend(queryClient))
    .use(initReactI18next)
    .init({
      supportedLngs: SUPPORTED_LANGUAGES,
      fallbackLng: DEFAULT_LANGUAGE,
      lng: getStoredLanguage(),
      ns: DEFAULT_NAMESPACES,
      defaultNS: "common",
      interpolation: { escapeValue: false },
      react: { useSuspense: true },
    });
}

export const i18n = i18next;
